import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Row,
    Col,
    Card,
    Tabs,
    Table,
    Button,
    Modal,
    Form,
    Input,
    Select,
    Space,
    Tag,
    Statistic,
    message,
    DatePicker,
    Alert,
    Tooltip,
    Collapse,
    Divider,
    Badge,
    Typography,
    theme,
} from 'antd';
import {
    PlusOutlined,
    AimOutlined,
    RocketOutlined,
    WarningOutlined,
    CalendarOutlined,
    FilterOutlined,
    EditOutlined,
    DeleteOutlined,
    ThunderboltOutlined,
    ExperimentOutlined,
    CommentOutlined,
    SendOutlined,
    BarChartOutlined,
    RiseOutlined,
    UnorderedListOutlined,
    DollarOutlined,
    InboxOutlined,
    LaptopOutlined,
    CarOutlined,
    LineChartOutlined,
    SoundOutlined,
} from '@ant-design/icons';
import { strategyApi, usersApi } from '../../api/client';
import InitiativeDetailTabs from './InitiativeDetailTabs';
import GoalDetailModal from './GoalDetailModal';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import dayjs from 'dayjs';
import { useNotificationRefresh } from '../../hooks/useNotificationRefresh';

const { TabPane } = Tabs;
const { Panel } = Collapse;
const { Text } = Typography;

// ============ ENUMS & CONSTANTS ============

const GOAL_STATUSES = [
    { value: 'ACTIVE', label: 'Aktywny', color: 'green' },
    { value: 'CLOSED', label: 'Zamknięty', color: 'default' },
];

const INITIATIVE_STATUSES = [
    { value: 'PLANNED', label: 'Planowana', color: 'default' },
    { value: 'ACTIVE', label: 'Aktywna', color: 'processing' },
    { value: 'ON_HOLD', label: 'Wstrzymana', color: 'warning' },
    { value: 'CLOSED', label: 'Zamknięta', color: 'success' },
    { value: 'ABANDONED', label: 'Porzucona', color: 'error' },
];

const PRIORITIES = [
    { value: 'P0', label: 'P0 — Krytyczna', color: 'red' },
    { value: 'P1', label: 'P1 — Ważna', color: 'orange' },
    { value: 'P2', label: 'P2 — Nice-to-have', color: 'blue' },
];

const MODES = [
    { value: 'CORE', label: 'Core — Pełne zaangażowanie' },
    { value: 'BACKGROUND', label: 'Background — W tle' },
];

const AREAS = [
    { value: 'sprzedaz', label: 'Sprzedaż' },
    { value: 'produkt', label: 'Produkt' },
    { value: 'IT', label: 'IT' },
    { value: 'logistyka', label: 'Logistyka' },
    { value: 'finanse', label: 'Finanse' },
    { value: 'marketing', label: 'Marketing' },
];

const LOAD_LEVELS = [
    { value: 'LOW', label: 'Niskie obciążenie', color: 'green' },
    { value: 'MEDIUM', label: 'Średnie obciążenie', color: 'orange' },
    { value: 'HIGH', label: 'Wysokie obciążenie', color: 'red' },
];

const HORIZON_OPTIONS = [
    { value: 'Q1', label: 'Q1' },
    { value: 'Q2', label: 'Q2' },
    { value: 'Q3', label: 'Q3' },
    { value: 'Q4', label: 'Q4' },
    { value: 'Q1-Q2', label: 'Q1-Q2' },
    { value: 'Q2-Q3', label: 'Q2-Q3' },
    { value: 'Q3-Q4', label: 'Q3-Q4' },
    { value: '2026', label: 'Cały rok 2026' },
];

// ============ INTERFACES ============

interface StrategicGoal {
    id: string;
    name: string;
    description?: string;
    reason?: string;
    keyResults?: string;
    year: number;
    status: string;
    initiatives?: StrategicInitiative[];
    comments?: GoalComment[]; // Using local interface
    owner?: { id: string; firstName: string; lastName: string };
}

interface GoalComment {
    id: string;
    content: string;
    createdAt: string;
    author: { id: string; firstName: string; lastName: string };
}

interface StrategicMilestone {
    id: string;
    name: string;
    dueDate: string;
    isCompleted: boolean;
}

interface StrategicInitiative {
    id: string;
    name: string;
    goalId?: string;
    goal?: StrategicGoal;
    businessObjective?: string;
    successMetrics?: string;
    goNoGoCriteria?: string;
    horizon?: string;
    startDate?: string;
    endDate?: string;
    priority: string;
    mode: string;
    estimatedLoad?: string;
    status: string;
    areas?: string[];
    notes?: string;
    milestones?: StrategicMilestone[];
    ownerId?: string;
    owner?: { id: string; firstName: string; lastName: string; };
}

interface OverloadWarning {
    message: string;
    initiatives: { id: string; name: string; priority: string }[];
    period: string;
}

interface GanttData {
    initiatives: StrategicInitiative[];
    milestones: StrategicMilestone[];
    warnings: OverloadWarning[];
}

interface OverviewStats {
    goalsCount: number;
    activeGoalsCount: number;
    initiativesCount: number;
    activeInitiativesCount: number;
    p0Count: number;
    byStatus: Record<string, number>;
    byPriority: Record<string, number>;
}

// ============ MAIN COMPONENT ============

export default function StrategyPage() {
    const [saveFilterForm] = Form.useForm();
    const { token } = theme.useToken();

    const queryClient = useQueryClient();
    const refreshNotifications = useNotificationRefresh();
    const [searchParams, setSearchParams] = useSearchParams();
    const [year] = useState(new Date().getFullYear());
    const [activeTab, setActiveTab] = useState('overview');

    // Filters
    const [filters, setFilters] = useState<{
        status?: string;
        priority?: string;
        area?: string;
        ownerId?: string;
    }>({});

    // Modals
    const [goalModalOpen, setGoalModalOpen] = useState(false);
    const [goalDetailOpen, setGoalDetailOpen] = useState(false);
    const [initiativeModalOpen, setInitiativeModalOpen] = useState(false);
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [detailActiveTab, setDetailActiveTab] = useState('info');
    const [selectedInitiative, setSelectedInitiative] = useState<StrategicInitiative | null>(null);
    const [selectedGoalId, setSelectedGoalId] = useState<string | null>(null);
    const [externalGoal, setExternalGoal] = useState<StrategicGoal | null>(null);



    // Forms
    const [goalForm] = Form.useForm();
    const [initiativeForm] = Form.useForm();
    // State for editing goal
    const [editingGoalId, setEditingGoalId] = useState<string | null>(null);

    const handleEditGoal = (goal: StrategicGoal) => {
        goalForm.setFieldsValue({
            ...goal,
        });
        setEditingGoalId(goal.id);
        setGoalModalOpen(true);
    };

    const handleGoalFormSubmit = (values: any) => {
        if (editingGoalId) {
            updateGoalMutation.mutate({ id: editingGoalId, data: values });
        } else {
            createGoalMutation.mutate(values);
        }
    };

    const handleDeleteGoal = (id: string) => {
        Modal.confirm({
            title: 'Usuń cel strategiczny',
            content: 'Czy na pewno chcesz usunąć ten cel? Wszystkie podpięte inicjatywy zostaną odpięte.',
            okText: 'Usuń',
            okType: 'danger',
            cancelText: 'Anuluj',
            onOk: () => deleteGoalMutation.mutate(id),
        });
    };

    const handleViewGoal = (goal: StrategicGoal) => {
        setSelectedGoalId(goal.id);
        setExternalGoal(null);
        setGoalDetailOpen(true);
    };

    // ReactQuill modules config
    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['link'],
            ['clean']
        ],
    };

    // ============ QUERIES ============

    const { data: overviewStats } = useQuery<OverviewStats>({
        queryKey: ['strategy-overview'],
        queryFn: () => strategyApi.getOverview().then((res: any) => res.data),
    });

    const { data: goals = [], isLoading: goalsLoading } = useQuery<StrategicGoal[]>({
        queryKey: ['strategy-goals', year],
        queryFn: () => strategyApi.getGoals(year).then((res: any) => res.data),
    });

    // Derive selected goal from list to ensure updates (e.g. comments) are reflected
    const selectedGoal = useMemo(() =>
        goals.find((g: StrategicGoal) => g.id === selectedGoalId) || externalGoal
        , [goals, selectedGoalId, externalGoal]);

    const { data: initiatives = [], isLoading: initiativesLoading } = useQuery<StrategicInitiative[]>({
        queryKey: ['strategy-initiatives', filters],
        queryFn: () => strategyApi.getInitiatives(filters).then((res: any) => res.data),
    });

    // Deep linking effect
    useEffect(() => {
        // Initiative deep link
        const initiativeId = searchParams.get('initiative');
        if (initiativeId) {
            const found = initiatives.find((i: StrategicInitiative) => i.id === initiativeId);
            if (found) {
                setSelectedInitiative(found);
                setDetailModalOpen(true);
            } else {
                strategyApi.getInitiative(initiativeId)
                    .then(res => {
                        setSelectedInitiative(res.data);
                        setDetailModalOpen(true);
                    })
                    .catch(() => {
                        message.warning('Nie znaleziono inicjatywy');
                        const newParams = new URLSearchParams(searchParams);
                        newParams.delete('initiative');
                        setSearchParams(newParams);
                    });
            }
        }

        // Goal deep link
        const goalId = searchParams.get('goal');
        if (goalId) {
            const found = goals.find((g: StrategicGoal) => g.id === goalId);
            if (found) {
                setSelectedGoalId(goalId);
                setExternalGoal(null);
                setGoalDetailOpen(true);
            } else {
                strategyApi.getGoal(goalId)
                    .then(res => {
                        setExternalGoal(res.data);
                        setSelectedGoalId(goalId);
                        setGoalDetailOpen(true);
                    })
                    .catch(() => {
                        message.warning('Nie znaleziono celu lub brak dostępu');
                        const newParams = new URLSearchParams(searchParams);
                        newParams.delete('goal');
                        setSearchParams(newParams);
                    });
            }
        }
    }, [searchParams, initiatives, goals]);

    const { data: ganttData, isLoading: ganttLoading } = useQuery<GanttData>({
        queryKey: ['strategy-gantt', year],
        queryFn: () => strategyApi.getGantt({ year }).then((res: any) => res.data),
    });

    const { data: usersData } = useQuery({
        queryKey: ['users-list'],
        queryFn: () => usersApi.getAll().then((res: any) => res.data),
    });

    const users = usersData || [];

    // Unique owners from initiatives (for filter)
    const uniqueOwners = useMemo(() => {
        const ownerMap = new Map<string, { id: string; firstName: string; lastName: string }>();
        initiatives.forEach((init: any) => {
            if (init.owner) {
                ownerMap.set(init.owner.id, init.owner);
            }
        });
        return Array.from(ownerMap.values());
    }, [initiatives]);

    // ============ MUTATIONS ============

    const createGoalMutation = useMutation({
        mutationFn: (data: any) => strategyApi.createGoal(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['strategy-goals'] });
            queryClient.invalidateQueries({ queryKey: ['strategy-overview'] });
            message.success('Cel dodany');
            setGoalModalOpen(false);
            goalForm.resetFields();
        },
        onError: () => message.error('Błąd przy dodawaniu celu'),
    });

    const createInitiativeMutation = useMutation({
        mutationFn: (data: any) => strategyApi.createInitiative(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['strategy-initiatives'] });
            queryClient.invalidateQueries({ queryKey: ['strategy-gantt'] });
            queryClient.invalidateQueries({ queryKey: ['strategy-overview'] });
            message.success('Inicjatywa dodana');
            setInitiativeModalOpen(false);
            initiativeForm.resetFields();
        },
        onError: () => message.error('Błąd przy dodawaniu inicjatywy'),
    });

    const updateInitiativeMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: any }) => strategyApi.updateInitiative(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['strategy-initiatives'] });
            queryClient.invalidateQueries({ queryKey: ['strategy-gantt'] });
            message.success('Inicjatywa zaktualizowana');
        },
        onError: () => message.error('Błąd przy aktualizacji'),
    });

    const deleteInitiativeMutation = useMutation({
        mutationFn: (id: string) => strategyApi.deleteInitiative(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['strategy-initiatives'] });
            queryClient.invalidateQueries({ queryKey: ['strategy-gantt'] });
            queryClient.invalidateQueries({ queryKey: ['strategy-overview'] });
            message.success('Inicjatywa usunięta');
        },
        onError: () => message.error('Błąd przy usuwaniu'),
    });

    const updateGoalMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: any }) => strategyApi.updateGoal(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['strategy-goals'] });
            message.success('Zaktualizowano cel');
            setGoalModalOpen(false);
            goalForm.resetFields();
        },
        onError: () => message.error('Błąd edycji celu'),
    });

    const deleteGoalMutation = useMutation({
        mutationFn: (id: string) => strategyApi.deleteGoal(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['strategy-goals'] });
            queryClient.invalidateQueries({ queryKey: ['strategy-overview'] });
            message.success('Usunięto cel');
        },
        onError: () => message.error('Błąd usuwania celu'),
    });

    // Goal Comments Mutations
    const createGoalCommentMutation = useMutation({
        mutationFn: ({ goalId, content }: { goalId: string; content: string }) => strategyApi.createGoalComment(goalId, { content }),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['strategy-goals'] });
            refreshNotifications();
            message.success('Dodano komentarz');
        },
    });

    const deleteGoalCommentMutation = useMutation({
        mutationFn: (id: string) => strategyApi.deleteGoalComment(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['strategy-goals'] });
            refreshNotifications();
            message.success('Usunięto komentarz');
        },
    });

    // ============ HANDLERS ============

    const handleViewInitiative = (initiative: StrategicInitiative) => {
        setSelectedInitiative(initiative);
        setDetailModalOpen(true);
    };

    const handleEditInitiative = (initiative: StrategicInitiative) => {
        initiativeForm.setFieldsValue({
            ...initiative,
            startDate: initiative.startDate ? dayjs(initiative.startDate) : undefined,
            endDate: initiative.endDate ? dayjs(initiative.endDate) : undefined,
        });
        setSelectedInitiative(initiative);
        setInitiativeModalOpen(true);
    };

    const handleDeleteInitiative = (id: string) => {
        Modal.confirm({
            title: 'Usuń inicjatywę',
            content: 'Czy na pewno chcesz usunąć tę inicjatywę?',
            okText: 'Usuń',
            okType: 'danger',
            cancelText: 'Anuluj',
            onOk: () => deleteInitiativeMutation.mutate(id),
        });
    };

    const handleInitiativeFormSubmit = (values: any) => {
        const data = {
            ...values,
            startDate: values.startDate?.toISOString(),
            endDate: values.endDate?.toISOString(),
        };

        if (selectedInitiative) {
            updateInitiativeMutation.mutate({ id: selectedInitiative.id, data });
            setInitiativeModalOpen(false);
            setSelectedInitiative(null);
            initiativeForm.resetFields();
        } else {
            createInitiativeMutation.mutate(data);
        }
    };

    // ============ COLUMNS ============

    const initiativeColumns = [
        {
            title: 'Priorytet',
            dataIndex: 'priority',
            key: 'priority',
            width: 100,
            render: (p: string) => {
                const priority = PRIORITIES.find(x => x.value === p);
                return <Tag color={priority?.color}>{p}</Tag>;
            },
            sorter: (a: any, b: any) => {
                const order = ['P0', 'P1', 'P2'];
                return order.indexOf(a.priority) - order.indexOf(b.priority);
            },
        },
        {
            title: 'Inicjatywa',
            dataIndex: 'name',
            key: 'name',
            render: (name: string, record: StrategicInitiative) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{name}</Text>
                    {record.businessObjective && (
                        <Text type="secondary" style={{ fontSize: 12 }}>{record.businessObjective}</Text>
                    )}
                </Space>
            ),
        },
        {
            title: 'Obszar',
            dataIndex: 'areas',
            key: 'areas',
            width: 150,
            render: (areas: string[]) => areas && areas.length > 0
                ? areas.map((a, idx) => <Tag key={idx}>{AREAS.find(x => x.value === a)?.label || a}</Tag>)
                : '-',
        },
        {
            title: 'Mode',
            dataIndex: 'mode',
            key: 'mode',
            width: 100,
            render: (m: string) => (
                <Tag icon={m === 'CORE' ? <ThunderboltOutlined /> : <ExperimentOutlined />} color={m === 'CORE' ? 'volcano' : 'cyan'}>
                    {m}
                </Tag>
            ),
        },
        {
            title: 'Horyzont',
            dataIndex: 'horizon',
            key: 'horizon',
            width: 100,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 120,
            render: (s: string) => {
                const status = INITIATIVE_STATUSES.find(x => x.value === s);
                return <Tag color={status?.color}>{status?.label || s}</Tag>;
            },
        },
        {
            title: 'Obciążenie',
            dataIndex: 'estimatedLoad',
            key: 'estimatedLoad',
            width: 100,
            render: (l: string) => {
                const load = LOAD_LEVELS.find(x => x.value === l);
                return load ? <Tag color={load.color}>{load.label}</Tag> : '-';
            },
        },
        {
            title: 'Właściciel',
            dataIndex: 'owner',
            key: 'owner',
            width: 140,
            render: (owner: any) => owner ? `${owner.firstName} ${owner.lastName}` : <Text type="secondary">-</Text>,
        },
        {
            title: 'Akcje',
            key: 'actions',
            width: 100,
            render: (_: any, record: StrategicInitiative) => (
                <Space>
                    <Tooltip title="Edytuj">
                        <Button size="small" icon={<EditOutlined />} onClick={(e) => { e.stopPropagation(); handleEditInitiative(record); }} />
                    </Tooltip>
                    <Tooltip title="Usuń">
                        <Button size="small" danger icon={<DeleteOutlined />} onClick={(e) => { e.stopPropagation(); handleDeleteInitiative(record.id); }} />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    // ============ GANTT VISUALIZATION ============

    const GanttTimeline = ({ data }: { data: GanttData }) => {
        const months = ['Sty', 'Lut', 'Mar', 'Kwi', 'Maj', 'Cze', 'Lip', 'Sie', 'Wrz', 'Paź', 'Lis', 'Gru'];

        const getBarStyle = (initiative: StrategicInitiative) => {
            if (!initiative.startDate || !initiative.endDate) return null;

            const start = new Date(initiative.startDate);
            const end = new Date(initiative.endDate);
            const yearStart = new Date(year, 0, 1);
            const yearEnd = new Date(year, 11, 31);

            const startPercent = Math.max(0, ((start.getTime() - yearStart.getTime()) / (yearEnd.getTime() - yearStart.getTime())) * 100);
            const endPercent = Math.min(100, ((end.getTime() - yearStart.getTime()) / (yearEnd.getTime() - yearStart.getTime())) * 100);
            const width = endPercent - startPercent;

            const priorityColors: Record<string, string> = {
                P0: '#ff4d4f',
                P1: '#fa8c16',
                P2: '#1890ff',
            };

            return {
                left: `${startPercent}%`,
                width: `${Math.max(width, 2)}%`,
                backgroundColor: priorityColors[initiative.priority] || '#1890ff',
            };
        };

        return (
            <div className="gantt-container">
                {/* Header */}
                <div className="gantt-header">
                    <div className="gantt-label-col"></div>
                    <div className="gantt-timeline">
                        {months.map((m, i) => (
                            <div key={i} className="gantt-month">{m}</div>
                        ))}
                    </div>
                </div>

                {/* Rows */}
                {data.initiatives.map((initiative) => {
                    const barStyle = getBarStyle(initiative);
                    return (
                        <div key={initiative.id} className="gantt-row">
                            <div className="gantt-label-col">
                                <Space>
                                    <Tag color={PRIORITIES.find(p => p.value === initiative.priority)?.color}>
                                        {initiative.priority}
                                    </Tag>
                                    <Text ellipsis style={{ maxWidth: 200 }}>{initiative.name}</Text>
                                </Space>
                            </div>
                            <div className="gantt-timeline">
                                {barStyle && (
                                    <Tooltip title={`${initiative.name} (${initiative.horizon})`}>
                                        <div
                                            className={`gantt-bar ${initiative.mode?.toLowerCase()}`}
                                            style={barStyle}
                                        />
                                    </Tooltip>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    // ============ RENDER ============

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* Overload Warnings */}
            {ganttData?.warnings && ganttData.warnings.length > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    icon={<WarningOutlined />}
                    message="Ostrzeżenie o przeciążeniu"
                    description={
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                            {ganttData.warnings.map((w, i) => (
                                <li key={i}>{w.message}</li>
                            ))}
                        </ul>
                    }
                />
            )}

            {/* Stats Cards */}
            <Row gutter={[16, 16]}>
                <Col xs={24} sm={6}>
                    <Card>
                        <Statistic
                            title="Cele aktywne"
                            value={overviewStats?.activeGoalsCount || 0}
                            suffix={`/ ${overviewStats?.goalsCount || 0}`}
                            prefix={<AimOutlined />}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={6}>
                    <Card>
                        <Statistic
                            title="Inicjatywy aktywne"
                            value={overviewStats?.activeInitiativesCount || 0}
                            suffix={`/ ${overviewStats?.initiativesCount || 0}`}
                            prefix={<RocketOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={6}>
                    <Card>
                        <Statistic
                            title="Inicjatywy P0"
                            value={overviewStats?.p0Count || 0}
                            prefix={<ThunderboltOutlined />}
                            valueStyle={{ color: '#ff4d4f' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={6}>
                    <Card>
                        <Statistic
                            title="W trakcie (ACTIVE)"
                            value={overviewStats?.byStatus?.ACTIVE || 0}
                            prefix={<CalendarOutlined />}
                            valueStyle={{ color: '#722ed1' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Main Content */}
            <Card>
                <Tabs activeKey={activeTab} onChange={setActiveTab}>
                    <TabPane tab={<span><BarChartOutlined style={{ marginRight: 6 }} />Przegląd</span>} key="overview">
                        <Row gutter={[24, 24]}>
                            {/* Goals */}
                            <Col span={24}>
                                <Card
                                    title={<span><AimOutlined style={{ marginRight: 6 }} />Cele strategiczne</span>}
                                    size="small"
                                    extra={
                                        <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setGoalModalOpen(true)}>
                                            Dodaj cel
                                        </Button>
                                    }
                                >
                                    {goals.length === 0 ? (
                                        <Text type="secondary">Brak celów na rok {year}</Text>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                            {goals.map((goal) => (
                                                <Card
                                                    key={goal.id}
                                                    size="small"
                                                    hoverable
                                                    onClick={() => handleViewGoal(goal)}
                                                    bordered={false}
                                                    style={{ background: token.colorFillAlter, border: `1px solid ${token.colorBorderSecondary}` }}
                                                >
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Space>
                                                            <Badge status={goal.status === 'ACTIVE' ? 'processing' : 'default'} />
                                                            <Text strong style={{ fontSize: 15 }}>{goal.name}</Text>
                                                            <Tag>{goal.year}</Tag>
                                                        </Space>
                                                        <Space>
                                                            <Tag color="geekblue">{goal.initiatives?.length || 0} inicjatyw</Tag>
                                                            {goal.owner && (
                                                                <Tooltip title={`Właściciel: ${goal.owner.firstName} ${goal.owner.lastName}`}>
                                                                    <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#87d068', textAlign: 'center', lineHeight: '24px', color: 'white', fontSize: 10 }}>
                                                                        {goal.owner.firstName[0]}{goal.owner.lastName[0]}
                                                                    </div>
                                                                </Tooltip>
                                                            )}
                                                        </Space>
                                                    </div>
                                                </Card>
                                            ))}
                                        </div>
                                    )}
                                </Card>
                            </Col>

                            {/* Priority breakdown */}
                            <Col span={12}>
                                <Card title={<span><RiseOutlined style={{ marginRight: 6 }} />Wg priorytetu</span>} size="small">
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        {PRIORITIES.map(p => (
                                            <div key={p.value} style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Tag color={p.color}>{p.label}</Tag>
                                                <Text strong>{overviewStats?.byPriority?.[p.value] || 0}</Text>
                                            </div>
                                        ))}
                                    </Space>
                                </Card>
                            </Col>
                            <Col span={12}>
                                <Card title={<span><UnorderedListOutlined style={{ marginRight: 6 }} />Wg statusu</span>} size="small">
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        {INITIATIVE_STATUSES.map(s => (
                                            <div key={s.value} style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Tag color={s.color}>{s.label}</Tag>
                                                <Text strong>{overviewStats?.byStatus?.[s.value] || 0}</Text>
                                            </div>
                                        ))}
                                    </Space>
                                </Card>
                            </Col>
                        </Row>
                    </TabPane>

                    <TabPane tab={<span><RocketOutlined style={{ marginRight: 6 }} />Inicjatywy</span>} key="initiatives">
                        <Space style={{ marginBottom: 16 }} wrap>
                            <Button type="primary" icon={<PlusOutlined />} onClick={() => {
                                setSelectedInitiative(null);
                                initiativeForm.resetFields();
                                setInitiativeModalOpen(true);
                            }}>
                                Dodaj inicjatywę
                            </Button>
                            <Divider type="vertical" />
                            <FilterOutlined />
                            <Select
                                placeholder="Status"
                                allowClear
                                style={{ width: 150 }}
                                options={INITIATIVE_STATUSES.map(s => ({ value: s.value, label: s.label }))}
                                value={filters.status}
                                onChange={(v) => setFilters({ ...filters, status: v })}
                            />
                            <Select
                                placeholder="Priorytet"
                                allowClear
                                style={{ width: 150 }}
                                options={PRIORITIES.map(p => ({ value: p.value, label: p.label }))}
                                value={filters.priority}
                                onChange={(v) => setFilters({ ...filters, priority: v })}
                            />
                            <Select
                                placeholder="Obszar"
                                allowClear
                                style={{ width: 150 }}
                                options={AREAS}
                                value={filters.area}
                                onChange={(v) => setFilters({ ...filters, area: v })}
                            />
                            <Select
                                placeholder="Właściciel"
                                allowClear
                                style={{ width: 180 }}
                                showSearch
                                optionFilterProp="label"
                                options={uniqueOwners.map(o => ({ value: o.id, label: `${o.firstName} ${o.lastName}` }))}
                                value={filters.ownerId}
                                onChange={(v) => setFilters({ ...filters, ownerId: v })}
                            />
                        </Space>
                        <Table
                            dataSource={initiatives}
                            columns={initiativeColumns}
                            rowKey="id"
                            loading={initiativesLoading}
                            size="small"
                            pagination={{ pageSize: 20 }}
                            onRow={(record) => ({
                                onClick: () => handleViewInitiative(record as StrategicInitiative),
                                style: { cursor: 'pointer' },
                            })}
                        />
                    </TabPane>

                    <TabPane tab={<span><CalendarOutlined style={{ marginRight: 6 }} />Oś czasu</span>} key="gantt">
                        <Card title={`Oś czasu inicjatyw — ${year}`} loading={ganttLoading}>
                            {ganttData && ganttData.initiatives.length > 0 ? (
                                <GanttTimeline data={ganttData} />
                            ) : (
                                <Text type="secondary">Brak inicjatyw z określonymi datami</Text>
                            )}
                        </Card>
                    </TabPane>
                </Tabs>
            </Card>


            <Modal
                title={editingGoalId ? "Edytuj cel strategiczny" : "Nowy cel strategiczny"}
                open={goalModalOpen}
                onCancel={() => {
                    setGoalModalOpen(false);
                    setEditingGoalId(null);
                    goalForm.resetFields();
                }}
                onOk={() => goalForm.submit()}
                confirmLoading={createGoalMutation.isPending || updateGoalMutation.isPending}
                width={800}
            >
                <Form
                    form={goalForm}
                    layout="vertical"
                    onFinish={handleGoalFormSubmit}
                    initialValues={{ year, status: 'ACTIVE' }}
                >
                    <Form.Item name="name" label="Nazwa celu" rules={[{ required: true }]}>
                        <Input placeholder="np. Zwiększenie sprzedaży o 50%" />
                    </Form.Item>

                    <Form.Item name="reason" label="Uzasadnienie biznesowe (Dlaczego to robimy?)">
                        <ReactQuill theme="snow" modules={quillModules} style={{ height: 150, marginBottom: 50 }} />
                    </Form.Item>

                    <Form.Item name="keyResults" label="Kluczowe rezultaty (Po czym poznamy sukces?)">
                        <ReactQuill theme="snow" modules={quillModules} style={{ height: 150, marginBottom: 50 }} />
                    </Form.Item>

                    <Form.Item name="description" label="Dodatkowy opis (opcjonalnie)">
                        <ReactQuill theme="snow" modules={quillModules} style={{ height: 100, marginBottom: 50 }} />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="year" label="Rok" rules={[{ required: true }]}>
                                <Select options={[
                                    { value: 2025, label: '2025' },
                                    { value: 2026, label: '2026' },
                                    { value: 2027, label: '2027' },
                                ]} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="status" label="Status">
                                <Select options={GOAL_STATUSES.map(s => ({ value: s.value, label: s.label }))} />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>

            {/* Initiative Modal */}
            <Modal
                title={selectedInitiative ? "Edytuj inicjatywę" : "Nowa inicjatywa strategiczna"}
                open={initiativeModalOpen}
                onCancel={() => {
                    setInitiativeModalOpen(false);
                    setSelectedInitiative(null);
                    initiativeForm.resetFields();
                }}
                onOk={() => initiativeForm.submit()}
                confirmLoading={createInitiativeMutation.isPending || updateInitiativeMutation.isPending}
                width={700}
            >
                <Form
                    form={initiativeForm}
                    layout="vertical"
                    onFinish={handleInitiativeFormSubmit}
                    initialValues={{ status: 'PLANNED', priority: 'P1', mode: 'CORE', estimatedLoad: 'MEDIUM' }}
                >
                    <Form.Item name="name" label="Nazwa inicjatywy" rules={[{ required: true }]}>
                        <Input placeholder="np. Wdrożenie integracji z Baselinker" />
                    </Form.Item>
                    <Form.Item name="businessObjective" label="Cel biznesowy (1 zdanie)">
                        <Input placeholder="np. Usprawnić proces realizacji zamówień z wielu kanałów" />
                    </Form.Item>
                    <Form.Item name="successMetrics" label="Metryki sukcesu (2-3 liczbowe)">
                        <ReactQuill theme="snow" modules={quillModules} style={{ height: 100, marginBottom: 50 }} />
                    </Form.Item>
                    <Form.Item name="goNoGoCriteria" label="Kryteria Go/No-Go">
                        <ReactQuill theme="snow" modules={quillModules} style={{ height: 100, marginBottom: 50 }} />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="priority" label="Priorytet" rules={[{ required: true }]}>
                                <Select options={PRIORITIES.map(p => ({ value: p.value, label: p.label }))} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="mode" label="Tryb" rules={[{ required: true }]}>
                                <Select options={MODES} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="estimatedLoad" label="Obciążenie">
                                <Select options={LOAD_LEVELS.map(l => ({ value: l.value, label: l.label }))} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="status" label="Status">
                                <Select options={INITIATIVE_STATUSES.map(s => ({ value: s.value, label: s.label }))} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="areas" label="Obszary">
                                <Select mode="multiple" options={AREAS} allowClear placeholder="Wybierz obszary" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="horizon" label="Horyzont">
                                <Select options={HORIZON_OPTIONS} allowClear />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="ownerId" label="Właściciel inicjatywy">
                        <Select
                            placeholder="Wybierz właściciela"
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            options={users.map((u: any) => ({
                                value: u.id,
                                label: `${u.firstName} ${u.lastName}`,
                            }))}
                        />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="startDate" label="Data startu">
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="endDate" label="Data końca">
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="goalId" label="Powiązany cel">
                        <Select
                            options={goals.map((g) => ({ value: g.id, label: g.name }))}
                            allowClear
                            placeholder="Wybierz cel (opcjonalnie)"
                        />
                    </Form.Item>

                    <Form.Item name="notes" label="Notatki">
                        <ReactQuill theme="snow" modules={quillModules} style={{ height: 100, marginBottom: 50 }} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Detail Modal */}
            <Modal
                title={`Inicjatywa: ${selectedInitiative?.name || ''}`}
                open={detailModalOpen}
                onCancel={() => {
                    setDetailModalOpen(false);
                    setSelectedInitiative(null);
                    setDetailActiveTab('info');
                }}
                footer={[
                    <Button key="close" onClick={() => setDetailModalOpen(false)}>Zamknij</Button>,
                    detailActiveTab === 'info' && (
                        <Button key="edit" type="primary" onClick={() => {
                            setDetailModalOpen(false);
                            if (selectedInitiative) handleEditInitiative(selectedInitiative);
                        }}>Edytuj</Button>
                    ),
                ].filter(Boolean)}
                width={1000}
            >
                {selectedInitiative && (
                    <InitiativeDetailTabs
                        initiative={selectedInitiative}
                        onTabChange={(key) => setDetailActiveTab(key)}
                    />
                )}
            </Modal>

            {/* Gantt CSS */}
            <style>{`
                .gantt-container {
                    width: 100%;
                    overflow-x: auto;
                }
                .gantt-header, .gantt-row {
                    display: flex;
                    border-bottom: 1px solid ${token.colorBorderSecondary};
                }
                .gantt-label-col {
                    width: 280px;
                    min-width: 280px;
                    padding: 8px;
                    border-right: 1px solid ${token.colorBorderSecondary};
                }
                .gantt-timeline {
                    flex: 1;
                    display: flex;
                    position: relative;
                    min-height: 36px;
                }
                .gantt-month {
                    flex: 1;
                    text-align: center;
                    padding: 8px 4px;
                    font-size: 12px;
                    font-weight: 500;
                    color: ${token.colorTextDescription};
                    border-right: 1px solid ${token.colorBorderSecondary};
                }
                .gantt-bar {
                    position: absolute;
                    height: 20px;
                    top: 50%;
                    transform: translateY(-50%);
                    border-radius: 4px;
                    opacity: 0.85;
                    cursor: pointer;
                    transition: opacity 0.2s;
                }
                .gantt-bar:hover {
                    opacity: 1;
                }
                .gantt-bar.core {
                    box-shadow: 0 0 0 2px rgba(255,77,79,0.3);
                }
                .gantt-bar.background {
                    opacity: 0.6;
                    background-image: repeating-linear-gradient(
                        45deg,
                        transparent,
                        transparent 5px,
                        rgba(255,255,255,0.1) 5px,
                        rgba(255,255,255,0.1) 10px
                    );
                }
            `}</style>
            {/* Goal Detail Modal */}
            <GoalDetailModal
                goal={selectedGoal}
                open={goalDetailOpen}
                onClose={() => setGoalDetailOpen(false)}
                onEdit={(goal) => {
                    setGoalDetailOpen(false);
                    handleEditGoal(goal);
                }}
                onDelete={(id) => {
                    setGoalDetailOpen(false);
                    handleDeleteGoal(id);
                }}
                onInitiativeClick={(initiative) => {
                    // setGoalDetailOpen(false); // Maybe keep it open? But we open another modal. AntD modals stack fine.
                    // But usually better to close parent context if it's "details -> details".
                    // However, user might want to go back.
                    // Let's keep Goal Modal open, and open Initiative Modal on top.
                    // The Initiative Detail modal logic uses 'selectedInitiative'.
                    // So we just call handleViewInitiative(initiative).
                    // But wait, handleViewInitiative uses 'setDetailModalOpen(true)' which is the View Modal (InitiativeDetailTabs).
                    handleViewInitiative(initiative);
                }}
            />
        </Space>
    );
}
